<template>
  <div>
    <div class="main-content bg-primary-white rounded-t-lg">
      <div class="border border-primary-gray-200 bg-white rounded-lg text-base capitalize p-5">
        <div class="pb-3 flex text-sm overflow-hidden">
          <span class="font-medium text-primary-gray-700 min-w-[160px]">Title:</span>
          <span class="font-normal text-primary-gray-600 line-clamp-2">
            {{ notificationTitle }}
          </span>
        </div>
        <div v-if="role" class="py-3 flex text-sm overflow-hidden">
          <span class="font-medium text-primary-gray-700 min-w-[160px]">Role:</span>
          <span class="font-normal text-primary-gray-600 line-clamp-2">
            {{ currentRole }}
          </span>
        </div>
        <div v-if="emailBody" class="py-3 flex text-sm">
          <span class="font-medium text-primary-gray-700 min-w-[160px]">Email Body:</span>
          <span
            class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none"
          >
            {{ emailBody }}
          </span>
        </div>
        <div v-if="pushBody" class="py-3 flex text-sm">
          <span class="font-medium text-primary-gray-700 min-w-[160px]">
            in-app notification Body:
          </span>
          <span
            class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none"
          >
            {{ pushBody }}
          </span>
        </div>
        <div v-if="smsBody" class="pt-3 flex text-sm">
          <span class="font-medium text-primary-gray-700 min-w-[160px]">Sms Body:</span>
          <span
            class="font-normal text-primary-gray-600 line-clamp-2 xl:line-clamp-3 xxl:line-clamp-none"
          >
            {{ smsBody }}
          </span>
        </div>
      </div>
      <div class="mt-5">
        <ToggleButton
          v-model="isDropdownSelectedMode"
          heading="Send to selected only"
          :is-status-hide="true"
          class="justify-end"
        />
        <InputFieldWrapper>
          <UiMultiSelect
            v-if="isDropdownSelectedMode"
            v-model="dropdownSelectedUserList"
            title="RECEIVERS"
            label="full_name"
            :image="true"
            :options="dropdownList"
            :already-selected="dropdownSelectedUserList"
            :disabled="!!isStatsDisplay"
            :parent-class="userClasses"
            input-color="bg-white"
            :search-through-api="true"
            @search="_getUsersData"
            @onChange="userChecked"
          />
        </InputFieldWrapper>
      </div>
      <template v-if="isLoading"><Loader class="mt-20 mb-40" :content="true" /></template>
      <div v-else-if="!isLoading && isEmpty(currentUsersList)">
        <NoRecordFound :text="isDropdownSelectedMode ? 'NO_SELECTED_USER' : 'NO_RECORD_FOUND'" />
      </div>
      <div v-else class="relative">
        <TableWrapper>
          <THead>
            <TRHead>
              <TH v-for="(head, index) in tableHead" :key="index">
                {{ head }}
              </TH>
              <TH v-if="selectedMedium.emailCheck">
                <span class="flex items-center">
                  <UiCheckBox
                    :model-value="selectAllEmails"
                    type="checkbox"
                    name="medium"
                    rules="required"
                    value="emailChecked"
                    :no-error="true"
                    @change="setEmailMediumForAllUsers($event)"
                  />
                  <span v-i18n="dashboard" class="ml-1">Email</span>
                </span>
              </TH>
              <TH v-if="selectedMedium.pushnotificationCheck">
                <span class="flex items-center">
                  <UiCheckBox
                    :model-value="selectAllPushs"
                    type="checkbox"
                    name="medium"
                    rules="required"
                    value="pushChecked"
                    :no-error="true"
                    @change="setPushMediumForAllUsers($event)"
                  />
                  <span v-i18n="dashboard" class="ml-1">Push Notification</span>
                </span>
              </TH>
              <TH v-if="selectedMedium.smsCheck">
                <span class="flex items-center">
                  <UiCheckBox
                    :model-value="selectAllSms"
                    type="checkbox"
                    name="medium"
                    :no-error="true"
                    rules="required"
                    value="smsChecked"
                    @change="setSmsMediumForAllUsers($event)"
                  />
                  <span v-i18n="dashboard" class="ml-1">Sms</span>
                </span>
              </TH>
            </TRHead>
          </THead>
          <TBody>
            <TRBody v-for="(user, index) in currentUsersList" :key="index">
              <TD>
                <BadgeDisplay
                  :options="[user]"
                  label="full_name"
                  :image="true"
                  :child-class="badgeWidth"
                />
              </TD>
              <TD v-if="selectedMedium.emailCheck">
                <UiCheckBox
                  :model-value="user.emailChecked"
                  :disabled="!user.email"
                  :no-error="true"
                  type="checkbox"
                  name="medium"
                  rules="required"
                  value="emailChecked"
                  @change="setSelectedMedium($event, user)"
                />
              </TD>
              <TD v-if="selectedMedium.pushnotificationCheck">
                <UiCheckBox
                  :model-value="user.pushChecked"
                  :no-error="true"
                  type="checkbox"
                  name="medium"
                  rules="required"
                  value="pushChecked"
                  :disabled="!user.login_activated_at"
                  @change="setSelectedMedium($event, user)"
                />
              </TD>
              <TD v-if="selectedMedium.smsCheck">
                <UiCheckBox
                  :model-value="user.smsChecked"
                  :disabled="!user.phone"
                  :no-error="true"
                  type="checkbox"
                  name="medium"
                  rules="required"
                  value="smsChecked"
                  @change="setSelectedMedium($event, user)"
                />
              </TD>
            </TRBody>
          </TBody>
        </TableWrapper>
      </div>
      <Pagination
        v-if="usersCount > RECORD_LIMIT"
        v-show="!isLoading"
        child-class="mr-0"
        :record-limit="RECORD_LIMIT"
        :max-range="paginationCounts(usersCount, RECORD_LIMIT)"
        @filterRecord="filterRecord"
      />
    </div>
  </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty'
import { objectDeepCopy, transformTextToCapitalize } from '@utils/generalUtil'
import { mapActions, mapState } from 'vuex'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import generalMixin from '@src/mixins/general-mixins.js'
import BadgeDisplay from '@components/UiElements/UiBadgeDisplay.vue'
import Pagination from '@components/BaseComponent/Pagination.vue'
import UiCheckBox from '@components/UiElements/UiCheckbox.vue'
import GENERAL from '@src/constants/general-constants'
import tooltipMixin from '@src/mixins/components/tooltip-mixin.js'
import { removeEmptyKeysFromObject } from '@utils/generalUtil.js'
import { paginationRangeHandler } from '@src/components/BaseComponent/pagination.util.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import { fullName } from '@/src/utils/settings/tenant-user.util'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import { getPageNumberWithRange } from '@src/components/BaseComponent/pagination.util.js'
import UiMultiSelect from '@/src/components/UiElements/UiMultiSelect.vue'
import ToggleButton from '@src/components/BaseComponent/toggleButton.vue'
import { TENANT_ROLES } from '@src/constants/user-roles-constants.js'

import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'
import InputFieldWrapper from '@/src/components/UiElements/InputFieldWrapper.vue'

export default {
  name: 'SendAnnouncements',
  components: {
    ToggleButton,
    UiMultiSelect,
    BadgeDisplay,
    UiCheckBox,
    Pagination,
    NoRecordFound,
    Loader,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
    InputFieldWrapper,
  },
  mixins: [generalMixin, tooltipMixin],
  props: {
    emailBody: { type: String, default: '' },
    smsBody: { type: String, default: '' },
    pushBody: { type: String, default: '' },
    notificationTitle: { type: String, default: '' },
    announcementType: { type: String, default: '' },
    role: { type: String, default: '' },
    section: { type: String, default: '' },
    currentClass: { type: String, default: '' },
    currentStudents: { type: String, default: '' },
    departmentId: { type: String, default: null },
    selectedMedium: { type: Object, default: () => ({}) },
    fetchCustomPayload: { type: Boolean, default: false },
  },

  emits: ['saveCustomNotification', 'selectedNotification'],
  data() {
    return {
      TENANT_ROLES,
      isDropdownSelectedMode: false,
      dropdownSelectedUserList: [],
      dropdownList: [],
      selectAllEmails: false,
      sendUsersList: [],
      selectAllPushs: false,
      selectAllSms: false,
      result: 'result',
      title: 'title',
      searchQuery: '',
      dashboard: 'dashboard',
      tableHead: ['Name'],
      noRecord: false,
      currentUsersList: [],
      allUsersList: [],
      usersCount: [],
      RECORD_LIMIT: GENERAL.MODAL_RECORD_LIMIT,
      forceUpdate: 1,
      updateStatusCount: 0,
      isLoading: false,
      UsersListOldLength: 0,
      usersWithPhone: [],
      usersWithEmail: [],
      usersLogin: [],
    }
  },
  computed: {
    badgeWidth() {
      return this.selectedMedium.emailCheck &&
        this.selectedMedium.pushnotificationCheck &&
        this.selectedMedium.smsCheck
        ? 'w-24 xxl:w-44'
        : 'w-24 md:w-44'
    },
    currentRole() {
      return transformTextToCapitalize(this.role)
    },
    ...mapState({
      showModal: (state) => state.layout.showModal,
    }),
  },
  watch: {
    dropdownSelectedUserList: {
      handler(Val) {
        if (Val.length !== this.UsersListOldLength) {
          this.updateUserList()
        }
        this.UsersListOldLength = Val.length
      },
      deep: true,
    },

    isDropdownSelectedMode: {
      immediate: true,
      handler() {
        this.allUsersList = []
        this.currentUsersList = []
        this.sendUsersList = []
        this.usersCount = []
        this.dropdownSelectedUserList = []
        this.$emit('selectedNotification', [])
        this.getUsersListBasedOnRole()
      },
      deep: true,
    },
    fetchCustomPayload: {
      handler() {
        this.saveNotification()
      },
    },
    updateStatusCount: {
      handler() {
        this.IsAllUsersSelectedFromusersList()
      },
    },
  },

  methods: {
    isEmpty,
    userChecked(list) {
      const { pushnotificationCheck, emailCheck, smsCheck } = this.selectedMedium
      list.forEach((user) => {
        if (pushnotificationCheck) user['pushChecked'] = true
        if (emailCheck) user['emailChecked'] = true
        if (smsCheck) user['smsChecked'] = true
      })
      this.sendUsersList = list
      this.$emit('selectedNotification', list)
    },
    updateUserList(range) {
      let paginationData = paginationRangeHandler(range, this.RECORD_LIMIT)
      this.usersCount = this.dropdownSelectedUserList.length
      this.currentUsersList = this.dropdownSelectedUserList.slice(
        paginationData.skip,
        paginationData.skip + paginationData.limit,
      )
    },
    async _getUsersData(query = '') {
      this.searchQuery = query
      this.getUsersListBasedOnRole()
    },

    filterRecord(range) {
      if (this.isDropdownSelectedMode) this.updateUserList(range)
      else this.getUsersListBasedOnRole(range)
    },
    async getUsersListBasedOnRole(range) {
      this.isLoading = true
      let paginationData = paginationRangeHandler(range, this.RECORD_LIMIT)
      let pageNo = getPageNumberWithRange(paginationData, this.RECORD_LIMIT)
      let isUsersListAlreadyExists = this.allUsersList.hasOwnProperty(`${pageNo}`)
      let response = null

      const filtersData = this.buildRoleFilter()
      const payload = { ...paginationData, ...filtersData }
      response = await this.getUsersByRole(payload).finally(() => {
        this.isLoading = false
      })
      response = response?.data || response || {}

      if (!this.isDropdownSelectedMode) {
        if (isUsersListAlreadyExists) {
          this.currentUsersList = this.allUsersList[`${pageNo}`] || []
          this.updateSelectionStatus()
        } else {
          this.currentUsersList = objectDeepCopy(response?.records) || []
          this.allUsersList = { ...this.allUsersList, [pageNo]: this.currentUsersList }
          this.resetSelectionStatus()
        }
        this.currentUsersList.forEach((user) => fullName(user))
        this.usersCount = response.meta?.total_records || this.GENERAL.EMPTY_RECORD
      } else {
        this.dropdownList = response?.records || []
      }

      this.isLoading = false
    },
    buildRoleFilter() {
      let filtersData = { $where: {} }

      switch (this.role) {
        case 'staff':
          filtersData.$where = { ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', this.searchQuery) }
          break
        case 'section_student':
          filtersData.$where = {
            ...buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', this.section),
            ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', this.searchQuery),
          }
          break
        case 'guardian':
          filtersData.$where = {
            ...buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', this.section),
            ...buildWhereQuery(FILTER_KEYS.IN, 'student_ids', this.currentStudents),
            ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', this.searchQuery),
          }
          break
        case 'section_teacher':
          filtersData.$where = {
            ...buildWhereQuery(FILTER_KEYS.EQUAL, 'section_id', this.section),
            ...buildWhereQuery(FILTER_KEYS.ILIKE, 'search', this.searchQuery),
          }
          break
      }

      removeEmptyKeysFromObject(filtersData.$where)
      return filtersData
    },
    async getUsersByRole(payload) {
      switch (this.role) {
        case 'staff':
          return await this.getStaffWithFilters(payload)
        case 'section_student':
          return await this.getStudentsWithFilters(payload)
        case 'guardian':
          return await this.getGuardiansWithFilter(payload)
        case 'section_teacher':
          return await this.getTeacherWithFilters(payload)
        default:
          return {}
      }
    },
    updateSelectionStatus() {
      if (this.selectedMedium.emailCheck) {
        this.usersWithEmail = this.currentUsersList.filter((user) => user.email)
        this.usersWithEmail.length
          ? (this.selectAllEmails = this.usersWithEmail.every((user) => !!user.emailChecked))
          : (this.selectAllEmails = false)
      }
      if (this.selectedMedium.pushnotificationCheck) {
        this.usersLogin = this.currentUsersList.filter((user) => user.login_activated_at)
        this.usersLogin.length
          ? (this.selectAllPushs = this.usersLogin.every((user) => !!user.pushChecked))
          : (this.selectAllPushs = false)
      }
      if (this.selectedMedium.smsCheck) {
        this.usersWithPhone = this.currentUsersList.filter((user) => user.phone)
        this.usersWithPhone.length
          ? (this.selectAllSms = this.usersWithPhone.every((user) => !!user.smsChecked))
          : (this.selectAllSms = false)
      }
    },

    resetSelectionStatus() {
      if (this.selectedMedium.emailCheck) this.selectAllEmails = false
      if (this.selectedMedium.pushnotificationCheck) this.selectAllPushs = false
      if (this.selectedMedium.smsCheck) this.selectAllSms = false
    },
    IsAllUsersSelectedFromusersList() {
      let allEmailsSelected = this.currentUsersList.every(
        (user) => user.emailChecked === true || !user.email,
      )
      let allPushsSelected = this.currentUsersList.every(
        (user) => user.pushChecked === true || !user.login_activated_at,
      )
      let allSmsSelected = this.currentUsersList.every(
        (user) => user.smsChecked === true || !user.phone,
      )
      this.selectAllEmails = allEmailsSelected
      this.selectAllPushs = allPushsSelected
      this.selectAllSms = allSmsSelected
    },
    setStatusByDefaultChecked() {
      // Email Check
      if (this.selectedMedium.emailCheck) {
        this.currentUsersList.forEach((user) => {
          user['emailChecked'] = true
        })
        this.selectAllEmails = true
      }
      // Push Notification Check

      if (this.selectedMedium.pushnotificationCheck) {
        this.currentUsersList.forEach((user) => {
          user['pushChecked'] = true
        })
        this.selectAllPushs = true
      }
      // SMS Service Check
      if (this.selectedMedium.smsCheck) {
        this.currentUsersList.forEach((user) => {
          user['smsChecked'] = true
        })
        this.selectAllSms = true
      }
    },

    saveNotification() {
      this.setStatusByDefaultChecked()
      let users = Object.values(objectDeepCopy(this.currentUsersList)).flat()
      users.forEach((user) => {
        user.mediums = []
        if (user.emailChecked && user.email) {
          user.mediums.push('email')
        }
        if (user.pushChecked && user.login_activated_at) {
          user.mediums.push('webpush')
        }
        if (user.smsChecked && user.phone) {
          user.mediums.push('sms')
        }
      })
      const payload = {
        receiver_role_id: this.role,
        receivers: users.filter((user) => user.mediums && user.mediums.length),
        department_id: this.departmentId,
        title: this.notificationTitle,
        message_body: this.smsBody,
        email_body: this.emailBody,
        webpush_body: this.pushBody,
        announcement_type_id: this.announcementType,
        section_id: this.section,
        class_id: this.currentClass,
      }

      this.$emit('saveCustomNotification', payload)
    },
    setSelectedMedium($event, user) {
      this.currentUsersList.forEach((item) => {
        if (item.id === user.id) {
          item[$event.value] = $event.status
        }
      })
      if ($event.status) {
        if (!this.sendUsersList.some((item) => item.id === user.id)) {
          this.sendUsersList.push(user)
        }
      } else {
        this.sendUsersList = this.sendUsersList.filter((item) => item.id !== user.id)
      }
      this.$emit('selectedNotification', this.sendUsersList)
      user[$event.value] = $event.status
      this.updateStatusCount++
    },

    setEmailMediumForAllUsers($event) {
      this.currentUsersList.forEach((user) => {
        if (user.email) user[$event.value] = $event.status
      })
      this.sendUsersList = this.currentUsersList
      this.selectAllEmails = $event.status
    },

    setPushMediumForAllUsers($event) {
      this.currentUsersList.forEach((user) => {
        user[$event.value] = $event.status
      })
      this.sendUsersList = this.currentUsersList
      this.selectAllPushs = $event.status
    },

    setSmsMediumForAllUsers($event) {
      this.currentUsersList.forEach((user) => {
        if (user.phone) {
          user[$event.value] = $event.status
        }
      })
      this.sendUsersList = this.currentUsersList
      this.selectAllSms = $event.status
    },
    ...mapActions('notifications', ['getStaffData', 'createAnnouncemnt']),
    ...mapActions('student', ['getStudentsWithFilters']),
    ...mapActions('guardian', ['getGuardiansWithFilter']),
    ...mapActions('staff', ['getStaffWithFilters']),
    ...mapActions('teacher', ['getTeacherWithFilters']),
  },
}
</script>
